import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { Typography, Grid } from "@material-ui/core";
import { format } from "date-fns";
import { Today } from "@material-ui/icons";
import Modal from "../../../app/components/modals/Modal";
import { translate as t } from "../../translate/localization";
import useStyles from "./styles";
import CobeeButton from "../../../app/components/CobeeButton";
import { MonetaryAmount } from "../../../app/domain/monetary-amount";
import CobeeTextField from "../../../app/components/CobeeTextField";
import CobeeDatePicker from "../../../app/components/CobeeDatePicker";
import { hideModal } from "../../../usecases/commons/hide-modal";
import TooltipIcon from "../../../app/components/TooltipIcon";
import ModalContext from "../../Contexts/components/ModalWrapper/context";
import EmployeeActionsContext from "../../Contexts/components/actions/EmployeeActionsWrapper/context";

export default function DeliverBagToEmployeesModal({
  withoutWrapper,
  isLoading,
  initialAmount,
  consumableFrom,
  onConfirm,
  periodDurationInPayrolls,
}) {
  const {
    container,
    buttonSeparation,
    datepicker,
    currencyLabel,
    tooltipRow,
    tooltipIcon,
    inputLabel,
  } = useStyles();

  const { inviteUploadingEmployeesIsLoading } = useContext(
    EmployeeActionsContext
  );

  const [amount, setAmount] = useState(
    MonetaryAmount.ofCurrency(initialAmount).toUnitary()
  );
  const [date, setDate] = useState(consumableFrom);

  const { closeModal } = useContext(ModalContext);

  const periodicity = (payrolls) => {
    const periods = {
      1: t("common-messages.month"),
      12: t("common-messages.year"),
    };

    return payrolls in periods ? periods[payrolls] : periods[12];
  };

  const modalContents = (
    <Grid item container xs={12}>
      <Grid container item xs={12} className={container}>
        <Grid item xs={12}>
          <Typography variant="h6">
            {t("subsidy-section.deliver-annual-bag-warning")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" className={inputLabel}>
            {t("subsidy-section.choose-amount-to-offer-message")}
          </Typography>
        </Grid>

        <Grid item xs={12} container className={tooltipRow} alignItems="center">
          <TooltipIcon className={tooltipIcon} disabled />
          <Typography variant="caption">
            {t("subsidy-section.current-bag-amount-message", {
              amount: MonetaryAmount.ofCurrency(initialAmount).prettifyMoney(
                {}
              ),
              currency: MonetaryAmount.defaultCurrency,
              periodicity: periodicity(periodDurationInPayrolls),
            })}
          </Typography>
        </Grid>

        <Grid item container xs={12} alignItems="center">
          <CobeeTextField
            type="number"
            minWidth={198}
            min={0}
            onChange={(e) => {
              setAmount(e.target.value);
            }}
            value={amount}
          />
          <Typography className={currencyLabel} variant="body2">{`${
            MonetaryAmount.defaultCurrency
          }/${t("common-messages.month")}`}</Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" className={inputLabel}>
            {t("subsidy-section.choose-date-to-offer-message")}
          </Typography>
        </Grid>

        <Grid item container xs={12}>
          <CobeeDatePicker
            onChange={(value) => setDate(value)}
            selectedDate={date}
            minDate={new Date()}
            customInput={
              <div className={datepicker}>
                <Grid
                  container
                  justify="space-between"
                  alignItems="center"
                  item
                  xs={12}
                >
                  <Typography variant="caption">
                    {format(date, "dd-MM-yyyy")}
                  </Typography>
                  <Today />
                </Grid>
              </div>
            }
          />
        </Grid>
      </Grid>
      <Grid item xs={12} container justify="center">
        <CobeeButton
          styleType="secondary"
          onClick={withoutWrapper ? closeModal : hideModal}
          className={buttonSeparation}
        >
          {t("actions.cancel")}
        </CobeeButton>
        <CobeeButton
          styleType="primary"
          onClick={() =>
            onConfirm({
              amount: MonetaryAmount.fromUnitary(Number(amount)),
              date,
            })
          }
          isLoading={isLoading || inviteUploadingEmployeesIsLoading}
          disabled={amount === ""}
        >
          {t("actions.accept")}
        </CobeeButton>
      </Grid>
    </Grid>
  );

  if (withoutWrapper) {
    return modalContents;
  }

  return (
    <Modal
      title={t("benefits-section.onboarding.what-is-a-benefit-plan")}
      horizontalPadding={0}
    >
      {modalContents}
    </Modal>
  );
}

DeliverBagToEmployeesModal.defaultProps = {
  isLoading: false,
  initialAmount: MonetaryAmount.ZERO,
  consumableFrom: new Date(),
  withoutWrapper: false,
};

DeliverBagToEmployeesModal.propTypes = {
  isLoading: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  initialAmount: PropTypes.instanceOf(MonetaryAmount),
  consumableFrom: PropTypes.instanceOf(Date),
  periodDurationInPayrolls: PropTypes.number.isRequired,
  withoutWrapper: PropTypes.bool,
};
